import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Card } from "@/core/components/ui/card";
import { Skeleton } from "@/core/components/ui/skeleton";
import { Stack } from "@/core/components/ui/stack";
import {
  EventCol,
  SpiffTypeCol,
  FlatTechnicianCol,
  SpiffDateCol,
  SpiffPaymentAmountCol,
  EventDateCol,
  SelectionWithUndismissCol,
} from "@/spiffs/common/spiff-payments-table";
import { ISpiff } from "@/spiffs/spiffs.d";
import { EnrichedUserWithSpiffs } from "@/user/user";

// Type for skeleton spiff with technician
interface SpiffWithTechnician extends ISpiff {
  technician: EnrichedUserWithSpiffs;
}

export const PayrollPeriodSkeleton = () => {
  // Use the same columns structure as the real table
  const columns: ColumnDef<SpiffWithTechnician>[] = useMemo(
    () =>
      [
        SelectionWithUndismissCol<SpiffWithTechnician>(() => {}),
        FlatTechnicianCol<SpiffWithTechnician>(),
        EventCol<SpiffWithTechnician>(),
        SpiffTypeCol<SpiffWithTechnician>(),
        EventDateCol<SpiffWithTechnician>(),
        SpiffDateCol<SpiffWithTechnician>(),
        SpiffPaymentAmountCol<SpiffWithTechnician>(),
      ].filter(Boolean) as ColumnDef<SpiffWithTechnician>[],
    [],
  );

  return (
    <div className="relative w-full">
      {/* Timeline dot skeleton */}
      <div className="absolute left-1 top-4 z-10">
        <Skeleton className="h-6 w-6 rounded-full" />
      </div>

      {/* Card skeleton with left margin for timeline space */}
      <div className="ml-8 mr-2 min-w-0">
        <Card className="grid max-h-[600px] min-h-0 w-full min-w-0 grid-rows-[auto_1fr] overflow-hidden">
          {/* Header skeleton */}
          <div className="border-b border-stroke-secondary p-4">
            <Stack direction="row" justify="between" align="center">
              <Stack gap="sm">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </Stack>
              <Stack direction="row" gap="sm">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
              </Stack>
            </Stack>
          </div>

          {/* Table skeleton using SimpleTable's loading state */}
          <div className="min-h-0 overflow-hidden">
            <SimpleTable<SpiffWithTechnician>
              columns={columns}
              data={[]}
              isLoading={true}
              loadingRowCount={5}
              scrollAreaClassName="pr-0"
              className="h-full min-h-0 w-full"
              tableClassName="min-w-[1200px]"
            />
          </div>
        </Card>
      </div>
    </div>
  );
};
